import { Button, Group, Stack, TextInput, Title } from "@mantine/core";
import type { FormArrayElement, UseFormReturnType } from "@mantine/form";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { emptyParent } from "../../models/parent";
import { ParticularsForm, type ParticularsFormErrorType, type ParticularsFormType } from "../particularsForm/ParticularsForm";

export type ParentFormType = {
  parents_attributes: ParticularsFormType &
    {
      id: number | null;
      email: string;
      phone: string;
    }[];
};

export type ParentErrors = ParticularsFormErrorType & {
  email?: string[];
};

type ParentFormProps<T extends ParentFormType> = {
  form: UseFormReturnType<T>;
  errors?: ParentErrors[];
};

export function ParentForm<T extends ParentFormType>({ form, errors }: ParentFormProps<T>) {
  const parentsLength = form.getValues().parents_attributes.length;
  const addParent = () => form.insertListItem("parents_attributes", emptyParent as FormArrayElement<T, "parents_attributes">);
  const removeParent = () => form.removeListItem("parents_attributes", parentsLength - 1);

  return (
    <Stack>
      {form.getValues().parents_attributes.map((parent, index) => (
        <Stack key={parent.id ?? `p${index}`} mb="md">
          <Title mb="0" order={3}>{`Elternteil ${index + 1}`}</Title>
          <ParticularsForm errors={errors?.[index]} form={form} path={`parents_attributes.${index}`} />
          <Group>
            <TextInput
              flex={1}
              withAsterisk
              type="email"
              label="E-Mail"
              key={form.key(`parents_attributes.${index}.email`)}
              {...form.getInputProps(`parents_attributes.${index}.email`)}
              error={errors?.[index]?.email}
            />
            <TextInput
              flex={1}
              type="tel"
              label="Telefonnummer"
              key={form.key(`parents_attributes.${index}.phone`)}
              {...form.getInputProps(`parents_attributes.${index}.phone`)}
            />
          </Group>
          {parentsLength - 1 === index && (
            <Button leftSection={<IconMinus />} variant="subtle" onClick={removeParent}>
              Elternteil entfernen
            </Button>
          )}
          {parentsLength < 2 && parentsLength - 1 === index && (
            <Button leftSection={<IconPlus />} variant="subtle" onClick={addParent}>
              Elternteil hinzufügen
            </Button>
          )}
        </Stack>
      ))}

      {parentsLength === 0 && (
        <Button leftSection={<IconPlus />} variant="subtle" onClick={addParent}>
          Elternteil hinzufügen
        </Button>
      )}
    </Stack>
  );
}
